import { useEffect } from 'react';
import { create } from 'zustand';
import { detectQuality, REDUCED_MOTION_QUERY } from './detect';
import type { NavigatorWithHints, QualityTier } from './detect';

interface QualityState {
  /** Conservative until the client has measured the device (the server/first render never pays for heavy effects). */
  tier: QualityTier;
  /** True once `refresh()` has run in a browser. */
  detected: boolean;
  /** Re-run detection, update the store and `<html data-q>`. */
  refresh: () => void;
}

export const useQualityStore = create<QualityState>()((set) => ({
  tier: 'lite',
  detected: false,
  refresh: () => {
    const tier = detectQuality();
    if (typeof document !== 'undefined') document.documentElement.dataset.q = tier;
    set({ tier, detected: true });
  },
}));

/** Current tier — `full` | `lite` | `minimal`. CSS can branch on `html[data-q="…"]` instead. */
export function useQuality(): QualityTier {
  return useQualityStore((state) => state.tier);
}

/**
 * Detect now and keep the tier current: re-evaluates when the reduced-motion media query or the
 * connection (Save-Data, effective type) changes. Returns the cleanup function.
 */
export function startQualityDetection(): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const { refresh } = useQualityStore.getState();
  refresh();

  const cleanups: (() => void)[] = [];

  if (typeof window.matchMedia === 'function') {
    try {
      const query = window.matchMedia(REDUCED_MOTION_QUERY);
      query.addEventListener('change', refresh);
      cleanups.push(() => {
        query.removeEventListener('change', refresh);
      });
    } catch {
      /* unsupported */
    }
  }

  const connection = (navigator as NavigatorWithHints).connection;
  if (connection?.addEventListener) {
    connection.addEventListener('change', refresh);
    cleanups.push(() => {
      connection.removeEventListener?.('change', refresh);
    });
  }

  return () => {
    for (const cleanup of cleanups) cleanup();
  };
}

/** Mount once (providers). */
export function useQualityDetection(): void {
  useEffect(() => startQualityDetection(), []);
}
